export interface FilterParam {
    name: string;
    type: keyof FilterParamTypes;
    active?: boolean;
    saved_filter_option?: FilterOption;
    saved_filter_value?: string;
}

export type FilterParamsFormulaInput = {
    name: string;
    option: string;
    input: string | number;
    tickers: string[];
};

export enum FilterParamTypes {
    Boolean = "Boolean",
    Float = "Float",
    String = "String",
    Integer = "Integer",
}

export type FilterOption = {
    label: string;
    value: string;
};

type Validator = (value: any) => boolean;

export type FilterOptions = {
    [key in FilterParamTypes]: {
        options: FilterOption[];
        expected_input: Validator | string[];
    };
};

export type ExpectedInput = string | Function;

export type FilterValue = {
    name: string;
    value: any;
    option: string;
};

export const filter_options: FilterOptions = {
    Boolean: {
        options: [
            {
                label: "Равно",
                value: "=",
            },
            {
                label: "Не равно",
                value: "!=",
            },
        ],
        expected_input: ["True", "False", "true", "false", "1", "0"],
    },
    String: {
        options: [
            {
                label: "Равно",
                value: "=",
            },
            {
                label: "Не равно",
                value: "!=",
            },
        ],
        expected_input: (input: string) => {
            if (typeof input !== "string") {
                return false;
            }
            return true;
        },
    },
    Float: {
        options: [
            {
                label: "=",
                value: "=",
            },
            {
                label: "!=",
                value: "!=",
            },
            {
                label: ">",
                value: ">",
            },
            {
                label: "<",
                value: "<",
            },
            {
                label: ">=",
                value: ">=",
            },
            {
                label: "<=",
                value: "<=",
            },
        ],
        expected_input: (input: number) => {
            if (!input) {
                return false;
            }
            let _input = input;

            _input = parseInt(input.toString());

            if (isNaN(_input)) {
                return false;
            }

            if (typeof _input !== "number") {
                return false;
            }

            return true;
        },
    },
    Integer: {
        options: [
            {
                label: "=",
                value: "=",
            },
            {
                label: "!=",
                value: "!=",
            },
            {
                label: ">",
                value: ">",
            },
            {
                label: "<",
                value: "<",
            },
            {
                label: ">=",
                value: ">=",
            },
            {
                label: "<=",
                value: "<=",
            },
        ],
        expected_input: (input: number) => {
            if (!input) {
                return false;
            }
            let _input = input;

            _input = parseInt(input.toString());

            if (isNaN(_input)) {
                return false;
            }

            if (typeof _input !== "number") {
                return false;
            }

            return true;
        },
    },
};

const allowed_options = Array.from(
    new Set(
        ...Object.keys(filter_options).map((t) => [
            ...filter_options[t].options.map((opt: FilterOption) => opt.value),
        ]),
    ),
);

const _lt = {
    virt: "B",
    stopped: "C",
    close: "D",
    mm: "E",
    no_open: "F",
    aggr_bid: "G",
    aggr_ask: "H",
    market_bid: "I",
    market_ask: "K",
    bid_open: "L",
    bid_close: "M",
    bid_hedge: "N",
    ask_open: "O",
    ask_close: "P",
    ask_hedge: "Q",
    bid_amount_close: "R",
    bid_amount_hedge: "S",
    bid_amount_open: "T",
    ask_amount_open: "U",
    ask_amount_close: "V",
    ask_amount_hedge: "W",
    cur_pos: "X",
    info: "Y",
};

const lt = {};

for (const key of Object.keys(_lt)) {
    lt[key] = _lt[key];
    if (key.includes("_")) {
        const _key = key.split("_").join(" ");
        lt[_key] = _lt[key];
    }
}

const validations = [
    {
        name: "is_string",
        message: "Переданная формула не является строкой.",
        fn: (input: string) => {
            const check = parseInt(input);
            if (!isNaN(check)) {
                return true;
            }
            return typeof input !== "string";
        },
    },
    {
        name: "has_internal_errors",
        message: "Переданная формула построена неправильно.",
        fn: (input: string) => {
            const patterns = ["undefined", "null"];
            for (const pattern of patterns) {
                if (input.includes(pattern)) {
                    return true;
                }
            }
            return false;
        },
    },
    {
        name: "has_incorrect_symbols",
        message: "Переданная формула имеет некорректные символы.",
        fn: (input: string) => {
            const patterns = [
                /^\p{Script=Cyrillic}+$/u,
                (_input: string) => {
                    const incorrect_symbols = [
                        "%",
                        "$",
                        "#",
                        "@",
                        "&",
                        "?",
                        ",",
                        "/",
                        "\n",
                        "|",
                        "{",
                        "}",
                        "[",
                        "]",
                        ";",
                        ":",
                        '"',
                        '"',
                        "'",
                        "`",
                    ];
                    return _input
                        .split("")
                        .some((sym) => incorrect_symbols.includes(sym));
                },
            ];

            for (const check of patterns) {
                if (typeof check === "function") {
                    const _check = check(input);
                    if (_check === true) {
                        return true;
                    }
                    continue;
                }
                //@ts-ignore
                const test = check.test(input);

                if (test === true) {
                    return true;
                }
            }
            return false;
        },
    },
    {
        name: "bullshit_received",
        message: "Формула не содержит ни одного условия.",
        fn: (input: string) => {
            const allowedValues = TickerParamsHandler.getAllowedValues();

            return !allowedValues.some((val) => input.includes(val));
        },
    },
];

export default class TickerParamsHandler {
    static _mnemonics: any[];
    static _values: FilterValue[] = [];
    static getAllowedValues() {
        return (this.mnemonics ?? []).map((item) => item.letter);
    }
    static get mnemonics() {
        return this._mnemonics;
    }
    static set mnemonics(mnem: any[]) {
        this._mnemonics = mnem;
    }
    static validateFormula(formula: string) {
        if (formula === "") {
            return true;
        }
        for (const check of validations) {
            const test = check.fn(formula);
            if (test) {
                return check.message;
            }
        }
        return true;
    }

    static buildFormula({ name, option, input }: FilterParamsFormulaInput) {
        const data = this.mnemonics.find((x) => x.name === name);
        this.addValue({
            name,
            option,
            value: input,
        });
        return `${data["letter"]}${option}${input}`;
    }

    static getFilterOptions() {
        return filter_options;
    }
    /**
     * true - input valid;
     * false - incorrect;
     */
    static checkInput(
        type: keyof FilterParamTypes,
        input: string | number,
    ): boolean {
        const expected_input = filter_options[type].expected_input;
        let test = false;
        if (typeof expected_input === "function") {
            test = expected_input(input);
            return test;
        }

        return expected_input.some((val: string) => val === input);
    }

    static processFilters(
        filters: FilterParamsFormulaInput[],
        _separator: "*" | "+",
    ) {
        let separator = _separator;
        const default_separator = "*";
        const _filters = this.mnemonics;
        if (!["*", "+"].includes(separator)) {
            separator = default_separator;
            //throw new Error(`Incorrect separator: ${separator}`);
        }

        const _formulas: string[] = [];
        const filter_names = _filters.map((x) => x.name.toLowerCase());
        for (const filter of filters) {
            if (!filter_names.includes(filter.name.toLowerCase())) {
                throw new Error(`Unknown filter name: ${filter.name}`);
            }
            const formula = this.buildFormula(filter);
            _formulas.push(formula);
        }

        return _formulas.join(separator);
    }

    static set values(_values: FilterValue[]) {
        this._values = _values;
    }

    static addValue(value: FilterValue) {
        this._values.push(value);
    }

    static get values() {
        return this._values;
    }

    static getFilterValue(name: string) {
        const filter = this._values.find((x) => x.name === name);
        if (filter) {
            return filter.value;
        }
    }

    static dropState() {
        this.values = [];
    }

    static getOption(name: string) {
        const x = this.values.find((x) => x.name === name);

        if (x) {
            return x.option;
        }
    }

    static parseFormula(formula: string): FilterParam[] {
        const separator = formula.includes("*") ? "*" : "+";
        const array = formula.split(separator);
        const filter_options: FilterParam[] = [];
        try {
            for (const part of array) {
                let letter = part[0];
                if (this.isLetter(part[1])) {
                    letter = part[0] + part[1];
                }

                const options = this._findAllowedOptions(letter).sort(
                    (a, b) => b.value.length - a.value.length,
                );
                const option = this._parseOption(part, options);
                const value = part.slice(letter.length + option.value.length);
                const { name, type } = this._findAssociatedData(letter);
                const filter_option = {
                    name,
                    type,
                    saved_filter_option: option,
                    saved_filter_value: value,
                };
                filter_options.push(filter_option);
            }
        } catch (error) {
            //console.error('er', error);
            return [];
        }

        return filter_options;
    }

    static _findAssociatedData(letter: string, name?: string) {
        const m = this.mnemonics;
        if (!m) {
            return undefined;
        }

        const mnem = name
            ? m.find((x) => x.name === name)
            : m.find((x) => x.letter === letter);
        if (!mnem) {
            throw `no data found for letter: ${letter}`;
        }

        return mnem;
    }

    static _findFilterName(letter: string) {
        const m = this.mnemonics;
        if (!m) {
            return undefined;
        }

        const mnem = m.find((x) => x.letter === letter);
        if (!mnem) {
            throw `no name found for letter: ${letter}`;
        }

        return mnem.name;
    }

    static _findAllowedOptions(letter: string) {
        const m = this.mnemonics;
        if (!m) {
            return [];
        }
        const mnem = m.find((x) => x.letter === letter);
        if (!mnem) {
            const message = `no mnemonic found for letter: ${letter}`;
            //console.error(message);
            return [];
        }
        const options = this.getFilterOptions();
        return options[mnem.type].options;
    }

    static _parseOption(formula: string, options: FilterOption[]) {
        for (const opt of options) {
            if (formula.includes(opt.value)) {
                return opt;
            }
        }
    }

    static isLetter(str: string) {
        return (
            typeof str === "string" && str.length === 1 && str.match(/[a-z]/i)
        );
    }
}
